// LIVE.ERROR_BOUNDARY_AND_CAPTURE / LIVE.CRASH_SELF_IDENTIFY - fire-and-forget
// client error reporter.
//
// Called from the app error boundaries and the global window hooks. Rule 17
// extended to the client: this must never throw and never block the fallback
// render. It uses navigator.sendBeacon (which survives an unload and never
// blocks the main thread) with a keepalive fetch fallback, and swallows every
// failure - a broken or missing capture endpoint degrades to a rendered
// fallback with no row, never a second error screen.
//
// The body is sent as text/plain so both transports stay CORS "simple requests"
// with no preflight (sendBeacon cannot preflight); the edge function parses the
// raw text as JSON regardless of content type. sendBeacon carries no auth
// header, which is why the sink has no user_id or tenant_id - we do not need
// caller identity to name a node.

import { showConnectionNotice } from "@/lib/connectionNotice";
import { getConnectivity } from "@/lib/connectivity";
import type { ClientErrorKind, RecognisedErrorKind } from "../../supabase/functions/_shared/client-error-kinds";
import { clearSkewGuardLater, isReleaseSkew, recoverFromSkew, skewSignal, trackTypedInput } from "@/lib/releaseSkew";

const MAX_FIELD = 8000;

// CLIENT.NETWORK_FAILURE_SILENT. The operator-facing copy for a lost connection.
// Deliberately makes NO claim about whether a save succeeded: `Failed to fetch`
// means the reply never arrived, not that the request never reached the server,
// so "not saved" could be false and would invite a duplicate action. Refresh and
// check is the only honest instruction.
export const CONNECTION_LOST_MESSAGE =
  "Lost connection to Trayvar. If you were saving something, refresh the page to check it went through.";

function errorText(reason: unknown): string {
  if (reason instanceof Error) return reason.message ?? "";
  return typeof reason === "string" ? reason : "";
}

/**
 * A fetch that never received a reply. `fetch()` rejects such a failure with a
 * TypeError whose message varies by browser ("Failed to fetch" in Chrome,
 * "NetworkError..." in Firefox, "Load failed" in Safari). This is the class the
 * save handlers never see, because they only read the returned error, not a
 * throw. Exported pure for the smoke.
 */
export function isTransportFailure(reason: unknown): boolean {
  const msg = errorText(reason);
  const isTypeError = reason instanceof TypeError;
  if (/failed to fetch|networkerror|network request failed|fetch failed/i.test(msg)) {
    return true;
  }
  // Safari's generic "Load failed" and bare "fetch"/"network" wording are only
  // trusted when the rejection is the TypeError shape fetch uses, so an
  // application error that happens to contain the word is not misread.
  if (isTypeError && /load failed|fetch|network/i.test(msg)) return true;
  // MAPS.TEARDOWN_AND_OFFLINE B: the shared connectivity source, not navigator.onLine.
  if (isTypeError && getConnectivity() === "offline") {
    return true;
  }
  return false;
}

/**
 * A code-split chunk that failed to load - a stale tab after a deploy. LIVE.POLISH
 * E: kept for its callers and smokes, but answered by the one release-skew check
 * (src/lib/releaseSkew.ts), which knows every form of a stale release, not only
 * the chunk wording. Exported pure for the smoke.
 */
export function isChunkLoadError(reason: unknown): boolean {
  return skewSignal(reason) === "definite";
}

/**
 * Reload at most once per session. LIVE.POLISH E: the decision now lives in
 * planSkewRecovery (releaseSkew.ts), which also never reloads over typed input;
 * this keeps the old contract for its smoke. Exported pure.
 */
export function planChunkReload(alreadyReloaded: boolean): boolean {
  return !alreadyReloaded;
}

/** LIVE.POLISH G: the kinds come from the one list the edge function and the
 *  table's CHECK (migration 244) share. */
export type ErrorKind = ClientErrorKind;

interface ReportOptions {
  error_kind?: ErrorKind;
  component_stack?: string;
  // The Next digest when the caller has one (server throws, some boundaries).
  digest?: string;
}

function endpoint(): string | null {
  const base = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!base) return null;
  return `${base.replace(/\/$/, "")}/functions/v1/log-client-error`;
}

// The first meaningful stack frame, used in the synthetic digest so identical
// faults collapse to one counted row even without a Next digest.
function firstStackFrame(stack: string): string {
  const lines = stack.split("\n").map((l) => l.trim()).filter(Boolean);
  const frame = lines.find((l) => l.startsWith("at ")) ?? lines[1] ?? lines[0] ?? "";
  return frame.slice(0, 300);
}

export function reportClientError(error: unknown, opts: ReportOptions = {}): void {
  try {
    if (typeof window === "undefined") return;
    const url = endpoint();
    if (!url) return;

    const err = error as { message?: unknown; stack?: unknown } | null;
    const message = String(err?.message ?? "Unknown client error").slice(0, MAX_FIELD);
    const stack = String(err?.stack ?? "").slice(0, MAX_FIELD);
    const errorKind = opts.error_kind ?? "uncaught";

    // Prefer the Next digest when the caller has one; otherwise a stable
    // synthetic digest so identical faults dedupe to one counted row. The sink
    // column is NOT NULL, so this is always a non-empty string.
    const digest =
      (opts.digest && opts.digest.trim()) ||
      `${errorKind}|${message}|${firstStackFrame(stack)}`;

    const payload = JSON.stringify({
      message,
      stack,
      route: window.location?.pathname ?? "",
      user_agent: navigator.userAgent ?? "",
      digest: digest.slice(0, MAX_FIELD),
      error_kind: errorKind,
      component_stack: (opts.component_stack ?? "").slice(0, MAX_FIELD),
    });

    if (typeof navigator.sendBeacon === "function") {
      const blob = new Blob([payload], { type: "text/plain;charset=UTF-8" });
      navigator.sendBeacon(url, blob);
      return;
    }

    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Swallow - the reporter must never break the fallback.
    });
  } catch {
    // Swallow - the reporter must never break the fallback.
  }
}

/**
 * LIVE.POLISH E and F: log a recognised, handled failure once per tab session, as
 * its own kind - a stale release (release_skew) or a dropped connection
 * (transport) - never as an error. Never throws.
 */
export function reportRecognisedOnce(kind: RecognisedErrorKind, error: unknown): void {
  try {
    const key = `trayvar:logged:${kind}`;
    if (typeof sessionStorage !== "undefined") {
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
    }
  } catch {
    // Storage blocked: log this one; the digest still collapses repeats to one row.
  }
  reportClientError(error, { error_kind: kind });
}

// Register the global client hooks once. window 'error' catches uncaught throws
// that no React boundary sees (the class that lands on the raw __next_error__
// screen), and 'unhandledrejection' catches rejected promises. Called from
// instrumentation-client.ts, which Next runs on the client before hydration.
let listenersRegistered = false;
export function registerGlobalErrorListeners(): void {
  try {
    if (typeof window === "undefined" || listenersRegistered) return;
    listenersRegistered = true;
    trackTypedInput();

    window.addEventListener("error", (event: ErrorEvent) => {
      const err =
        event.error ??
        new Error(event.message || "window error");
      // LIVE.POLISH E: a stale release (a chunk the new release no longer has, a
      // module webpack cannot find) is logged once as release_skew and recovered:
      // one guarded reload, or the prompt where a reload could lose typed input.
      if (skewSignal(err) === "definite") {
        reportRecognisedOnce("release_skew", err);
        recoverFromSkew(false);
        return;
      }
      reportClientError(err, { error_kind: "window_error" });
    });

    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const err =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === "string" ? reason : "unhandled rejection");
      void (async () => {
        // LIVE.POLISH E: every form of a stale release, through the one check (the
        // ambiguous forms only when the deployed build differs from this tab's).
        if (await isReleaseSkew(reason)) {
          reportRecognisedOnce("release_skew", err);
          recoverFromSkew(false);
          return;
        }
        // CLIENT.NETWORK_FAILURE_SILENT / LIVE.POLISH F: a fetch that never got
        // its reply shows the connection notice and is logged once as transport,
        // not as an error. Anything else is logged exactly as before.
        if (isTransportFailure(reason)) {
          reportRecognisedOnce("transport", err);
          showConnectionNotice(CONNECTION_LOST_MESSAGE);
          return;
        }
        reportClientError(err, { error_kind: "unhandled_rejection" });
      })();
    });

    // Clear the reload guard once the page has stayed up a few seconds, so a
    // later release can reload once again; a reload loop cannot clear it.
    clearSkewGuardLater(5000);
  } catch {
    // Never let the registration itself throw during startup.
  }
}
